import { toast } from "@/components/Toast";
import { Captcha_Words, useTranslate } from "@/utils/lang";
import { Editor_Words } from "@/utils/lang/editor";
import {
  Button,
  Center,
  Flex,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { getRefreshCaptcha, postVerifyCaptcha } from "../../apis/captcha";

export type CaptchaDataType = {
  captcha_id: string;
  captcha: string;
};

export enum CaptchaEvent {
  SHOW = "show",
}
const _CaptchaModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  onFailed?: (error?: any) => void;
  captcha: CaptchaDataType;
}> = ({ isOpen, onClose, captcha, onSuccess, onFailed }) => {
  const { translate } = useTranslate();
  const [data, setData] = useState(captcha);
  const [inputValue, setInputValue] = useState("");

  useEffect(() => {
    if (captcha.captcha_id !== data.captcha_id) {
      setData(captcha);
    }
  }, [captcha]);

  function handleClose() {
    onClose();
    setInputValue("");
  }

  function handleSubmit() {
    if (!inputValue) {
      toast({
        status: "warning",
        title: translate(Captcha_Words.validate_input),
      });
      return;
    }
    postVerifyCaptcha({
      id: data.captcha_id,
      input: inputValue,
    })
      .then((res) => res)
      .then((res) => {
        if (res?.code === 0) {
          onSuccess?.();
          handleClose();
          toast({
            status: "success",
            title: translate(Captcha_Words.success_message),
          });
        } else {
          Promise.reject(res);
        }
      })
      .catch((err) => {
        onFailed?.(err);
        toast({
          status: "error",
          title: translate(Captcha_Words.fail_message),
        });
        setInputValue("");
        handleRefresh();
      });
  }

  function handleRefresh() {
    getRefreshCaptcha(data.captcha_id)
      .then((res) => res)
      .then((res) => {
        const { data } = res;
        if (data) {
          setData(data);
        }
      });
  }
  return !data.captcha_id ? null : (
    <Modal size="sm" isOpen={isOpen} onClose={handleClose} isCentered>
      <ModalOverlay></ModalOverlay>
      <ModalContent bg="bg.panelBg" pb="40px" color="white">
        <ModalCloseButton />
        <ModalHeader>{translate(Captcha_Words.validate_title)}</ModalHeader>
        <ModalBody>
          <InputGroup variant="flushed" mt="24px">
            <InputLeftElement
              w="150px"
              h="48px"
              p="4px"
              bottom="0px"
              top="unset"
              alignItems="flex-end"
            >
              <Image src={data.captcha} w="100%" bg="#fff" />
            </InputLeftElement>
            <Input
              pl="150px"
              h="48px"
              placeholder={translate(Captcha_Words.captcha_input_placeholder)}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              borderColor="text.secondary"
              _placeholder={{ fontSize: "14px", color: "text.secondary" }}
              _focusVisible={{
                borderColor: "brand.primary",
                borderBottomWidth: "2px",
              }}
              textAlign="center"
            />
          </InputGroup>
          <Text
            w="100%"
            textAlign="right"
            fontSize="12px"
            as="button"
            mt="6px"
            onClick={handleRefresh}
          >
            {translate(Captcha_Words.refresh_captcha)}
          </Text>
          <Center mt="32px">
            <Button w="140px" variant="outline" onClick={handleClose} _notLast={{ mr: "8px" }}>
              {translate(Captcha_Words.cancel)}
            </Button>
            <Button w="140px" onClick={handleSubmit}>
              {translate(Captcha_Words.submit)}
            </Button>
          </Center>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export const CaptchaModal = observer(_CaptchaModal);
